import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  useAddCardRequirements,
  useSaveNewCard,
} from "../../../hooks/useBilling";
import { useTheme } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";
import { Icon } from "../UIElements/Icon";
import { Skeleton } from "../Skeletons";

const getStripePromise = (publishableKey) => {
  if (!publishableKey) return null;
  return import("@stripe/stripe-js").then(({ loadStripe }) =>
    loadStripe(publishableKey),
  );
};

const CardInputForm = ({ clientSecret, onSuccess, onCancel }) => {
  const { t } = useTranslation(["billing"]);
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToastContext();
  const saveNewCard = useSaveNewCard();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleChange = (event) => {
    setCardError(event?.error?.message ?? null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setCardError(result.error.message);
        return;
      }

      const paymentMethodId = result.setupIntent.payment_method;
      const savedCard = await saveNewCard.mutateAsync({
        source_id: paymentMethodId,
      });

      toast.success(t("cardSavedSuccessfully"));
      onSuccess?.(savedCard);
    } catch (err) {
      toast.error(err?.message || t("cardSaveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="border-border bg-muted rounded-lg border p-3">
        <CardElement
          onChange={handleChange}
          options={{
            style: {
              base: {
                fontSize: "14px",
                color: "var(--color-foreground, #fff)",
                "::placeholder": {
                  color: "var(--color-muted-foreground, #888)",
                },
              },
              invalid: { color: "#ef4444" },
            },
          }}
        />
      </div>
      {cardError && <p className="text-destructive text-xs">{cardError}</p>}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground border-border flex-1 rounded-lg border px-4 py-2 text-sm transition-colors"
        >
          {t("cancelAddCard")}
        </button>
        <button
          type="submit"
          disabled={!stripe || isSubmitting}
          className="bg-primary hover:bg-primary/90 text-primary-foreground flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Icon name="Loader" className="h-4 w-4 animate-spin" />
              {t("savingCard")}
            </span>
          ) : (
            t("saveCard")
          )}
        </button>
      </div>
    </form>
  );
};

const AddCardForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation(["billing"]);
  const theme = useTheme();
  const {
    data: requirements,
    isLoading,
    isError,
  } = useAddCardRequirements(true);

  const stripePromiseRef = useRef(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (requirements?.publishable_key && !stripePromiseRef.current) {
      stripePromiseRef.current = getStripePromise(requirements.publishable_key);
      setStripePromise(stripePromiseRef.current);
    }
  }, [requirements?.publishable_key]);

  if (isLoading) {
    return <Skeleton className="h-24 w-full rounded-lg" />;
  }

  if (isError || !requirements?.client_secret) {
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm">{t("cardSaveFailed")}</p>
        <button
          type="button"
          onClick={onCancel}
          className="text-primary hover:text-primary/80 text-sm font-medium"
        >
          {t("cancelAddCard")}
        </button>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="flex items-center justify-center py-4">
        <Icon
          name="Loader"
          className="text-muted-foreground h-6 w-6 animate-spin"
        />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: requirements.client_secret,
        appearance: {
          theme: theme === "dark" ? "night" : "stripe",
          variables: { colorPrimary: "#8a42ff" },
        },
      }}
    >
      <CardInputForm
        clientSecret={requirements.client_secret}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default AddCardForm;
